import type { TileMapRenderer } from '../../renderer/tilemap/TileMapRenderer';
import type { GameState } from '../../model/gamestate/GameState';
import type { PlayerSnapshot, SpaceTimeClient } from '../../spacetime/SpaceTimeClient';
import type { DebugWindow } from '../../ui/DebugWindow';
import type { EntityIdCardHandle } from '../../ui/EntityIdCard';
import type { SelfCharacterWindowHandle } from '../../ui/SelfCharacterWindow';
import type { StatusBarHandle } from '../../ui/StatusBar';
import type { EventHub } from '../../events/EventHub';
import { createMapEventPlaybackController } from './mapEventPlayback';
import { track } from './utils';

interface RuntimeConfig {
  instanceId: string;
  playerId: string;
}

interface PlayerInformation {
  entityId: string;
  displayName: string;
  guildName?: string;
  hp: number;
  maxHp: number;
  rotation: number;
}

interface PlayerIdentityInfo {
  displayName: string;
  guildName?: string;
}

interface PlayerRuntimeInfo {
  entityId: string;
  hp: number;
  maxHp: number;
  rotation: number;
}

interface CreateMapSubscriptionManagerParams {
  client: SpaceTimeClient;
  config: RuntimeConfig;
  gameState: GameState;
  renderer: TileMapRenderer;
  debugWindow: DebugWindow;
  statusBar: StatusBarHandle | null;
  entityIdCard: EntityIdCardHandle;
  selfCharacterWindow: SelfCharacterWindowHandle;
  eventHub: EventHub;
  onMyPlayerUpdate: (player: PlayerSnapshot | null) => void;
}

export interface MapSubscriptionManager {
  start(): Promise<void>;
  stop(): void;
  getPlayerInformation(playerId: string): PlayerInformation | undefined;
}

export function createMapSubscriptionManager(params: CreateMapSubscriptionManagerParams): MapSubscriptionManager {
  const {
    client,
    config,
    gameState,
    renderer,
    debugWindow,
    statusBar,
    entityIdCard,
    selfCharacterWindow,
    eventHub,
    onMyPlayerUpdate,
  } = params;

  let mapSubscriptions: Array<{ unsubscribe: () => void }> = [];
  const playerInformationByPlayerId = new Map<string, PlayerInformation>();
  const playerIdentityByPlayerId = new Map<string, PlayerIdentityInfo>();
  const playerRuntimeByPlayerId = new Map<string, PlayerRuntimeInfo>();
  let latestPlayerEntities: import('../../spacetime/types').RuntimeEntitySnapshot[] = [];
  const mapEventPlayback = createMapEventPlaybackController({
    playerId: config.playerId,
    gameState,
    renderer,
    debugWindow,
    eventHub,
  });

  const stop = (): void => {
    mapSubscriptions.forEach((subscription) => subscription.unsubscribe());
    mapSubscriptions = [];
    playerInformationByPlayerId.clear();
    playerIdentityByPlayerId.clear();
    playerRuntimeByPlayerId.clear();
    latestPlayerEntities = [];
    mapEventPlayback.stop();
    console.log('[Subscriptions] Map-dependent subscriptions stopped');
  };

  const refreshOpenPlayerProfiles = (): void => {
    const openProfile = entityIdCard.getCurrentProfile();
    const openRemotePlayerId =
      openProfile && openProfile.kind === 'remotePlayer' ? openProfile.playerId : undefined;
    if (!openProfile || !openRemotePlayerId) {
      return;
    }
    const live = playerInformationByPlayerId.get(openRemotePlayerId);
    if (!live) {
      return;
    }
    entityIdCard.open({
      ...openProfile,
      actorId: live.entityId || openProfile.actorId,
      name: live.displayName || openProfile.name,
      guildName: live.guildName,
      hp: live.hp,
      maxHp: live.maxHp,
      rotation: live.rotation,
    });
  };

  const rebuildPlayerInformation = (): void => {
    playerInformationByPlayerId.clear();
    for (const [playerId, runtime] of playerRuntimeByPlayerId.entries()) {
      const identity = playerIdentityByPlayerId.get(playerId);
      playerInformationByPlayerId.set(playerId, {
        entityId: runtime.entityId,
        displayName: identity?.displayName ?? (playerId === config.playerId ? config.playerId : 'Player'),
        guildName: identity?.guildName,
        hp: runtime.hp,
        maxHp: runtime.maxHp,
        rotation: runtime.rotation,
      });
    }
    refreshOpenPlayerProfiles();
  };

  const updateRemotePlayersFromLatestRuntime = (): void => {
    const remotes = latestPlayerEntities
      .filter((entity) => entity.playerId !== config.playerId)
      .map((entity) => {
        const playerId = entity.playerId ?? entity.entityId;
        const identity = playerIdentityByPlayerId.get(playerId);
        return {
          entityId: entity.entityId,
          playerId,
          displayName: identity?.displayName ?? playerId,
          entityType: entity.entityType,
          position: { x: entity.mapX ?? entity.position.x, y: entity.mapY ?? entity.position.y },
          prevMapX: entity.prevMapX,
          prevMapY: entity.prevMapY,
          rotation: entity.rotation ?? 180,
          color: entity.appearanceColorHex ?? '#3b82f6',
          accessoriesJson: entity.appearanceAccessoriesJson ?? '{}',
          hp: entity.hp,
          maxHp: entity.maxHp,
          mana: entity.mana,
          maxMana: entity.maxMana,
          stamina: entity.stamina,
          maxStamina: entity.maxStamina,
        };
      });
    gameState.updateRemotePlayers(remotes);
  };

  const start = async (): Promise<void> => {
    mapEventPlayback.start();

    const myPlayerSub = await client.players.subscribeToMyPlayer({
      onUpdate: onMyPlayerUpdate
    });
    track(mapSubscriptions, myPlayerSub);

    const visiblePlayerIdentitySub = await client.players.subscribeToVisiblePlayerIdentity({
      onUpdate: (players) => {
        playerIdentityByPlayerId.clear();
        for (const player of players) {
          playerIdentityByPlayerId.set(player.playerId, {
            displayName: player.displayName,
            guildName: player.guildName,
          });
        }
        rebuildPlayerInformation();
        updateRemotePlayersFromLatestRuntime();
        selfCharacterWindow.refresh();
        renderer.requestRender();
      }
    });
    track(mapSubscriptions, visiblePlayerIdentitySub);

    const runtimeEntitiesSub = await client.players.subscribeToRuntimeEntitiesForInstance(
      {
        onUpdate: (entities) => {
          const isPlayerEntity = (entity: (typeof entities)[number]): boolean =>
            entity.entityClass === 'player' || entity.entityType === 'player';
          const playerEntities = entities.filter(isPlayerEntity);
          latestPlayerEntities = playerEntities;
          playerRuntimeByPlayerId.clear();
          for (const entity of playerEntities) {
            if (!entity.playerId) {
              continue;
            }
            playerRuntimeByPlayerId.set(entity.playerId, {
              entityId: entity.entityId,
              hp: entity.hp ?? 0,
              maxHp: entity.maxHp ?? 0,
              rotation: entity.rotation ?? 180,
            });

            if (entity.playerId === config.playerId) {
              selfCharacterWindow.refresh();
            }
          }
          rebuildPlayerInformation();
          const selfEntity =
            playerEntities.find((entity) => entity.playerId === config.playerId) ?? null;
          updateRemotePlayersFromLatestRuntime();

          if (selfEntity) {
            gameState.setSelfEntityId(selfEntity.entityId);
            gameState.setSelfAppearance(
              selfEntity.entityType,
              selfEntity.appearanceColorHex ?? '#3b82f6',
              selfEntity.appearanceAccessoriesJson ?? '{}'
            );
            statusBar?.update({
              hp: selfEntity.hp,
              maxHp: selfEntity.maxHp,
              mana: selfEntity.mana,
              maxMana: selfEntity.maxMana,
              stamina: selfEntity.stamina,
              maxStamina: selfEntity.maxStamina,
            });
            const selfPos = { x: selfEntity.mapX ?? selfEntity.position.x, y: selfEntity.mapY ?? selfEntity.position.y };
            gameState.applyAuthoritativeState(selfPos, selfEntity.rotation ?? gameState.getCharacterRotation());
            selfCharacterWindow.refresh();
          }

          const mobs = entities
            .filter((entity) => entity.entityClass !== 'player' && entity.entityType !== 'player')
            .map((entity) => ({
              entityId: entity.entityId,
              entityType: entity.entityType,
              color: entity.appearanceColorHex ?? '#ef4444',
              accessoriesJson: entity.appearanceAccessoriesJson ?? '{}',
              mobInventoryValue: entity.mobInventoryValue ?? null,
              mobInventoryItemsJson: entity.mobInventoryItemsJson ?? null,
              prevMapX: entity.prevMapX,
              prevMapY: entity.prevMapY,
              rotation: entity.rotation,
              hp: entity.hp,
              maxHp: entity.maxHp,
              mana: entity.mana,
              maxMana: entity.maxMana,
              stamina: entity.stamina,
              maxStamina: entity.maxStamina,
              position: { x: entity.mapX ?? entity.position.x, y: entity.mapY ?? entity.position.y }
            }));
          gameState.updateRuntimeMobs(mobs);
          renderer.requestRender();
        }
      }
    );
    track(mapSubscriptions, runtimeEntitiesSub);

    const mapEventsSub = await client.mapEvents.subscribeToMapEvents((events) => {
      mapEventPlayback.handleEvents(events);
    });
    track(mapSubscriptions, mapEventsSub);

    const activeZonesSub = await client.skills.subscribeToActiveZones((zones) => {
      renderer.setActiveZones(zones);
      renderer.requestRender();
    });
    track(mapSubscriptions, activeZonesSub);

    const npcRuntimeSub = await client.inventory.subscribeToNpcRuntime((npcs) => {
      gameState.updateNpcRuntime(npcs.map((n) => ({
        npcId: n.npcId,
        instanceId: n.instanceId,
        mapId: n.mapId,
        x: n.x,
        y: n.y,
        npcType: n.npcType,
        metadataJson: n.metadataJson,
        despawnTick: n.despawnTick,
      })));
      renderer.requestRender();
    });
    track(mapSubscriptions, npcRuntimeSub);

    const chestInventorySub = await client.inventory.subscribeToChestInventory((rows) => {
      gameState.updateChestInventory(rows.map((r) => ({
        entryId: r.entryId,
        npcId: r.npcId,
        slotIndex: r.slotIndex,
        itemId: r.itemId,
        quantity: r.quantity,
        metadataJson: r.metadataJson,
      })));
    });
    track(mapSubscriptions, chestInventorySub);
  };

  return {
    start,
    stop,
    getPlayerInformation: (playerId: string) => playerInformationByPlayerId.get(playerId),
  };
}
